import { useCallback } from "react";
import { resolveCrmLead } from "../../../api/crmLeadResolve";
import { useCrmSession } from "../../../context/CrmSessionContext";
import { useCrmResource } from "../../../hooks/useCrmResource";
import CrmLeadLink from "./CrmLeadLink";
import LeadPersonProfile from "./LeadPersonProfile";
import LeadTickets from "./LeadTickets";
import LeadTimeline from "./LeadTimeline";

/**
 * Grupo de paneles CRM del aside de la ficha (perfil de persona, tickets, link
 * a la ficha del CRM y timeline). Resuelve UNA vez el id de la ficha canónica
 * del CRM (ver crmLeadResolve.ts: en un lead re-registrado el CRM mantiene la
 * ficha bajo el ULID antiguo) y se lo pasa a los cuatro paneles, para que
 * tickets/timeline/link apunten a la ficha real y no a un ULID-alias vacío.
 *
 * Con sesión CRM activa se espera a la resolución ANTES de montar los paneles:
 * montarlos ya con el ULID de Operations dispararía fetches con el id
 * equivocado y un flash de "Sin tickets" en los leads re-registrados. Sin
 * sesión (o mientras se comprueba) se montan directamente — cada panel pinta su
 * propio CrmConnectGate, exactamente como hasta ahora.
 */
export default function CrmLeadPanels({
  leadUlid,
  email,
}: {
  leadUlid: string;
  email: string | null;
}) {
  const { connected } = useCrmSession();
  const fetchResolution = useCallback(
    () => resolveCrmLead(leadUlid, email),
    [leadUlid, email],
  );
  // `connected` en deps: al conectar con el CRM (gate → recheck) la resolución
  // se relanza; el 401 previo la dejó silenciada con data=null.
  const { data, error } = useCrmResource(
    fetchResolution,
    [leadUlid, email, connected],
    "No se pudo resolver la ficha del CRM",
  );

  if (connected && !data && !error) {
    return (
      <div style={{ color: "#73879C", fontSize: ".82rem", padding: "0.5rem 0" }}>
        Cargando datos del CRM…
      </div>
    );
  }

  // Con error de resolución (nunca un 401; resolveCrmLead ya degrada a
  // fallback los fallos esperables) se usa el ULID de Operations tal cual:
  // mismo comportamiento que antes de existir la resolución.
  const crmLeadId = data?.crmLeadId ?? leadUlid;

  return (
    <>
      <LeadPersonProfile leadUlid={crmLeadId} />
      <LeadTickets leadUlid={crmLeadId} />
      {/* El aviso se muestra siempre que la ficha del CRM viva bajo OTRO id,
          tanto por tombstone (via 'ulid') como por match de email. */}
      <CrmLeadLink leadUlid={crmLeadId} redirected={crmLeadId !== leadUlid} />
      <LeadTimeline leadUlid={crmLeadId} />
    </>
  );
}
